<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form @submit="addVente" :validation-schema="venteSchema">
        <!-- Caisse utilisée pour cette vente -->
        <div class="row gx-2 mb-2">
          <div class="col-md-6">
            <div v-if="ouverturesChargees && ouvertures.length === 0" class="alert alert-warning mb-2 py-2">
              Aucune caisse ouverte pour ce point de vente. Ouvrez une caisse avant d'enregistrer une vente.
            </div>
            <div v-else-if="ouvertures.length === 1" class="form-group mb-0">
              <label class="d-block text-black fw-semibold">Caisse</label>
              <input type="text" :value="ouvertures[0].caisse.nom" readonly
                class="form-control shadow-none fs-md-15 text-black fw-bold" />
            </div>
            <div v-else-if="ouvertures.length > 1" class="form-group mb-0">
              <label class="d-block text-black fw-semibold">
                Caisse <span class="text-danger">*</span>
              </label>
              <select v-model="ouvertureId" class="form-select shadow-none fs-md-15 text-black fw-bold"
                :class="{ 'is-invalid': !ouvertureId }">
                <option :value="null" disabled>Choisir la caisse de cette vente</option>
                <option v-for="o in ouvertures" :key="o.id" :value="o.id">
                  {{ o.caisse.nom }} (ouverte le {{ formatDateOuverture(o.dateOuverture) }})
                </option>
              </select>
              <span class="invalid-feedback">Choisissez la caisse qui encaisse cette vente</span>
            </div>
          </div>
        </div>

        <div class="row gx-2">
          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="d-block text-black fw-semibold">Date commande</label>
              <Field name="dateCommande" :readonly="true" :value="getCurrentDate()" type="date"
                class="form-control shadow-none fs-md-15 text-black" />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-2">
              <label class="d-block text-black fw-semibold">
                Client <span class="text-danger">*</span>
                <a style="float: right" v-if="!selectedClient" class="btn btn-success me-3 btn-xs b-r-6"
                  data-bs-toggle="modal" data-bs-target="#AddClientModal" @click="toggleAbonne">
                  <i class="fa fa-plus-circle"></i>
                  Ajouter un client
                </a>
                <button type="button" style="float: right" v-if="selectedClient" class="btn btn-success me-3 btn-xs"
                  @click="toggleAbonne">
                  <i class="fa fa-plus-circle"></i>
                  Rechercher un client
                </button>
              </label>
              <Field name="client" v-slot="{ field }" v-if="!selectedClient" v-model="clientCharger">
                <Multiselect :options="clientOptions" :searchable="true" track-by="label" label="label"
                  v-model="field.value" v-bind="field" placeholder="Sélectionner le client" />
              </Field>
              <Field name="client" v-model="selectedClientId" v-slot="{ field }" v-else>
                <Multiselect v-model="field.value" :options="clientsOps" placeholder="Sélectionner un client" />
              </Field>
              <ErrorMessage name="client" class="text-danger" />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="d-block text-black fw-semibold">Total</label>
              <input type="text" :value="totalCommande" readonly
                class="form-control shadow-none fs-md-15 text-black fw-bold" />
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="d-block text-black fw-semibold">Montant reçu</label>
              <input type="number" min="0" v-model.number="montantRecuSaisi" :placeholder="String(totalCommande)"
                :class="montantInsuffisant ? 'form-control is-invalid' : 'form-control'"
                class="shadow-none fs-md-15 text-black fw-bold" />
              <span class="invalid-feedback">Inférieur au total</span>
            </div>
          </div>

          <div class="col-md-2">
            <div class="form-group mb-2">
              <label class="d-block text-black fw-semibold">Reliquat à rendre</label>
              <input type="text" :value="reliquat" readonly
                :class="reliquat > 0 ? 'text-success' : 'text-black'"
                class="form-control shadow-none fs-md-15 fw-bold" />
            </div>
          </div>
        </div>

        <div class="border border-primary mb-5 mt-3">
          <div class="row gx-2 d-flex align-items-center justify-content-between fw-bold py-2 bg-primary">
            <div class="col-md-7">
              <h4 class="fs-6 text-white">Ajouter le produit</h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button
                  class="default-btn me-20 transition border-0 fw-medium text-white pt-2 pb-2 ps-8 pe-8 rounded-1 fs-md-13 fs-lg-14 bg-success"
                  type="button" :class="{ 'cursor-not-allowed': isDisable }" :disabled="isDisable"
                  @click="addRowProduit()">
                  <i class="fa fa-plus-circle"></i>
                  {{ enModification ? 'Mettre à jour le produit' : 'Ajouter un produit' }}
                </button>
              </div>
            </div>
          </div>

          <div class="card-body p-1">
            <div class="row gx-1">
              <div class="col-md-4">
                <label class="d-block text-black fw-semibold">Produit</label>
                <Multiselect :options="produitOptions" :searchable="true" track-by="label" label="label"
                  v-model="produit.produit" placeholder="Sélectionnez le produit"
                  @select="selectProd(produit.produit, produit)" />
              </div>
              <div class="col">
                <label class="d-block text-black fw-semibold">Quantité</label>
                <input type="number" min="0" v-model.number="produit.qtite"
                  :class="validateRowProduit(produit.qtite) ? 'form-control is-invalid' : 'form-control'"
                  placeholder="Entrer la quantité" />
                <span class="invalid-feedback">Champ obligatoire</span>
              </div>
              <div class="col">
                <label class="d-block text-black fw-semibold">
                  Prix unitaire
                  <small v-if="produit.prixMin != null || produit.prixMax != null" class="text-muted fw-normal">
                    ({{ produit.prixMin ?? 0 }} – {{ produit.prixMax ?? '∞' }})
                  </small>
                </label>
                <input type="number" min="0" v-model.number="produit.prix"
                  :class="prixInvalide(produit) ? 'form-control is-invalid' : 'form-control'"
                  placeholder="Entrer le prix" />
                <span class="invalid-feedback">{{ messagePrix(produit) }}</span>
              </div>
              <div class="col" v-if="moduleGStockActif">
                <label class="d-block text-black fw-semibold">Magasin</label>
                <Multiselect :options="magasin1Options" :searchable="true" track-by="label" label="label"
                  v-model="produit.magasin" placeholder="" @select="updateStockByMagasin(produit)" />
              </div>
              <div class="col" v-if="moduleGStockActif">
                <label class="d-block text-black fw-semibold">Stock</label>
                <p class="mb-1">Physique : {{ produit.stockPhysique || 0 }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-fixed text-nowrap align-middle mb-0 table-bordered">
            <thead>
              <tr>
                <th scope="col" class="info">Produit</th>
                <th scope="col">Quantité</th>
                <th scope="col">Prix unitaire</th>
                <th scope="col" v-if="moduleGStockActif">Magasin</th>
                <th scope="col" class="actions pe-0">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(prod, index) in produits" :key="index">
                <td class="produit-col"><span class="info">{{ prod.libelleProduit }}</span></td>
                <td>{{ prod.qtite }}</td>
                <td>{{ prod.prix }}</td>
                <td v-if="moduleGStockActif">{{ getMagasinLabel(prod.magasin) }}</td>
                <td class="actions-col pe-0">
                  <div class="button-container" style="white-space: nowrap">
                    <button type="button" class="btn btn-warning transition border-0 pb-2 ps-8 pe-8"
                      @click="editProduit(index)">
                      <i class="icofont icofont-ui-edit"></i>
                    </button>
                    <button type="button" class="btn btn-danger transition border-0 pb-2 ps-8 pe-8"
                      @click="removeRowProduit(index)">
                      <i class="icofont icofont-ui-delete"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex align-items-center gap-3 mt-4">
          <button type="submit" class="btn btn-success px-4" :disabled="isSaving || produits.length === 0 || montantInsuffisant || !ouvertureId">
            <i class="fa fa-save me-2"></i> Enregistrer
          </button>
          <router-link to="/ventes/liste-ventes" class="btn btn-danger">
            <i class="fa fa-times me-1"></i> Annuler
          </router-link>
        </div>
      </Form>
    </div>
  </div>

  <AddClientModal @getclient="getClient" />
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, reactive, computed } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import { useRouter } from "vue-router";
import ApiService from "@/services/ApiService";
import * as Yup from "yup";
import axios from "axios";
import Multiselect from "@vueform/multiselect";
import AddClientModal from "./AddClientModal.vue";
import { error, success, warning } from "@/utils/utils";
import JwtService from "@/services/JwtService";

export default defineComponent({
  name: "AddVente",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
    AddClientModal,
  },
  setup: () => {
    const venteSchema = Yup.object().shape({
      client: Yup.string().required("Le client est obligatoire"),
    });

    const router = useRouter();
    const magasin1Options = ref<any[]>([]);
    const produitOptions = ref<any[]>([]);
    const clientOptions = ref<any[]>([]);
    const clientCharger = ref(null);
    const selectedClient = ref(false);
    const selectedClientId = ref(0);
    const clientsOps = ref<Array<{ value: any; label: string }>>([]);
    const isSaving = ref(false);

    // Sessions de caisse ouvertes par l'utilisateur : le caissier choisit explicitement laquelle encaisse
    interface OuvertureOuverte {
      id: number;
      dateOuverture: string;
      caisse: { id: number; nom: string };
    }
    const ouvertures = ref<OuvertureOuverte[]>([]);
    const ouvertureId = ref<number | null>(null);
    const ouverturesChargees = ref(false);
    const CLE_CAISSE = "venteOuvertureId";

    const formatDateOuverture = (d: string) =>
      d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "";

    const chargerOuvertures = async () => {
      try {
        const { data } = await ApiService.get(`/ouvfers/mes-ouvertures?pointvente=${getPointVenteId()}`);
        ouvertures.value = data.data || [];

        if (ouvertures.value.length === 1) {
          ouvertureId.value = ouvertures.value[0].id;
        } else if (ouvertures.value.length > 1) {
          // Reprend le dernier choix s'il est toujours valide, sinon le caissier doit choisir
          let dernier: number | null = null;
          try {
            dernier = Number(sessionStorage.getItem(CLE_CAISSE)) || null;
          } catch (_) { /* stockage indisponible */ }
          ouvertureId.value = ouvertures.value.some((o) => o.id === dernier) ? dernier : null;
        }
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la récupération des caisses ouvertes");
      } finally {
        ouverturesChargees.value = true;
      }
    };

    watch(ouvertureId, (id) => {
      try {
        if (id) sessionStorage.setItem(CLE_CAISSE, String(id));
      } catch (_) { /* stockage indisponible */ }
    });

    const toggleAbonne = () => {
      selectedClient.value = !selectedClient.value;
    };

    // Client créé via le modal : on le sélectionne directement
    const getClient = async (values: {
      id: any;
      nomClient: string;
      prenomClient: string;
      raisonSociale: string;
    }) => {
      try {
        selectedClientId.value = values.id;
        const data = {
          value: values.id,
          label: values?.nomClient
            ? values?.nomClient + "" + values?.prenomClient
            : "" + values?.raisonSociale,
        };
        clientsOps.value = [...clientsOps.value, data];
        clientCharger.value = values.id;
        return clientsOps.value;
      } catch (error) {
        // Gérer l'erreur ici
      }
    };
    const moduleGStockActif = ref(true); // true par défaut, mis à jour au chargement

    const produits = ref<any[]>([]);
    const enModification = ref(false);
    const modificationIndex = ref<number | null>(null);
    const isDisable = ref(true);

    const produit = reactive({
      produit: null as any,
      libelleProduit: "",
      qtite: 0,
      prix: 0,
      prixMin: null as number | null,
      prixMax: null as number | null,
      magasin: null as any,
      stockPhysique: 0,
    });

    const getCurrentDate = () => {
      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      return `${today.getFullYear()}-${month}-${day}`;
    };

    // Total autocalculé : somme quantité × prix de toutes les lignes
    const totalCommande = computed(() =>
      produits.value.reduce(
        (total, p) => total + (Number(p.qtite) || 0) * (Number(p.prix) || 0),
        0
      )
    );

    // Montant reçu saisi par le caissier ("" = paiement exact, égal au total)
    const montantRecuSaisi = ref<number | string>("");

    const montantRecuEffectif = computed(() =>
      montantRecuSaisi.value === "" || montantRecuSaisi.value === null
        ? totalCommande.value
        : Number(montantRecuSaisi.value)
    );

    const montantInsuffisant = computed(
      () => montantRecuEffectif.value < totalCommande.value
    );

    // Reliquat à rendre = montant reçu - total (jamais négatif)
    const reliquat = computed(() =>
      Math.max(0, Math.round((montantRecuEffectif.value - totalCommande.value) * 100) / 100)
    );

    const validateRowProduit = (value: any) => !value || value <= 0;

    const fetchMagasin1 = async () => {
      try {
        const response = await ApiService.get("/all/all/magasins");
        magasin1Options.value = response.data.data.map((magasin: any) => ({
          value: magasin.id + "|" + magasin.nomMagasin,
          label: magasin.code + " - " + magasin.nomMagasin + " du " + magasin.magasin.nomMagasin,
        }));
      } catch (error) {
        //
      }
    };

    const fetchProduits = async () => {
      try {
        const response = await axios.get("/all/produitconditionnements");
        produitOptions.value = response.data.data.map((pc: any) => ({
          // value : id | libellé | prixVente | prixMin | prixMax
          value:
            pc.id + "|" +
            `${pc.produit?.nomProd} [ ${pc.conditionnement?.libelle} ]` + "|" +
            (pc.prixVente ?? "") + "|" +
            (pc.prixMin ?? "") + "|" +
            (pc.prixMax ?? ""),
          label: `${pc.produit?.refProd ? pc.produit.refProd : pc.produit?.codeProduit} : ${pc.produit?.nomProd} [ ${pc.conditionnement?.libelle} ]`,
        }));
      } catch (error) {
        //
      }
    };

    const fetchClients = async () => {
      try {
        const response = await ApiService.get(`/all/clients`);
        clientOptions.value = response.data.data.map((client: any) => ({
          value: client.id,
          label: `${client?.nomClient
            ? client?.nomClient + " " + client?.prenomClient
            : client?.raisonSociale
            }`,
        }));
      } catch (error) {
        //
      }
    };

    const getMagasinLabel = (magasinId: any) => {
      const magasin = magasin1Options.value.find((m) => m.value === magasinId);
      return magasin ? magasin.label : "Non défini";
    };

    const toNumOrNull = (v: string | undefined) =>
      v !== undefined && v !== "" && !isNaN(Number(v)) ? Number(v) : null;

    // Au choix d'un produit : prix = prixVente par défaut, bornes = prixMin / prixMax
    const selectProd = (selectedProduit: string, p: any) => {
      const parts = (selectedProduit || "").split("|");
      p.libelleProduit = parts[1] || "";
      p.prix = Number(parts[2]) || 0;
      p.prixMin = toNumOrNull(parts[3]);
      p.prixMax = toNumOrNull(parts[4]);
      p.qtite = 1;
      p.stockPhysique = 0;
      if (p.magasin) {
        updateStockByMagasin(p);
      }
    };

    const prixInvalide = (p: any) => {
      if (!p.prix || p.prix <= 0) return true;
      if (p.prixMin !== null && p.prix < p.prixMin) return true;
      if (p.prixMax !== null && p.prix > p.prixMax) return true;
      return false;
    };

    const messagePrix = (p: any) => {
      if (!p.prix || p.prix <= 0) return "Champ obligatoire";
      if (p.prixMin !== null && p.prix < p.prixMin) return `Le prix ne peut pas être inférieur à ${p.prixMin}`;
      if (p.prixMax !== null && p.prix > p.prixMax) return `Le prix ne peut pas dépasser ${p.prixMax}`;
      return "";
    };

    // Stock physique du produit dans le magasin choisi
    const updateStockByMagasin = async (p: any) => {
      if (!p.produit || !p.magasin) {
        p.stockPhysique = 0;
        return;
      }
      const produitId = p.produit.split("|")[0];
      const magasinId = typeof p.magasin === "object" ? p.magasin.value : p.magasin;
      try {
        const response = await ApiService.get(`/stock/produit/${produitId}/magasin/${magasinId}`);
        p.stockPhysique = Number(response.data.data.qtite || 0);
      } catch (error) {
        console.error("Erreur lors de la récupération du stock:", error);
        p.stockPhysique = 0;
      }
    };

    const resetLigne = () => {
      produit.produit = null;
      produit.libelleProduit = "";
      produit.qtite = 0;
      produit.prix = 0;
      produit.magasin = null;
      produit.stockPhysique = 0;
      produit.prixMin = null;
      produit.prixMax = null;
    };

    const addRowProduit = () => {
      if (!produit.produit) {
        error("Veuillez sélectionner un produit");
        return;
      }
      if (validateRowProduit(produit.qtite)) {
        error("Veuillez saisir une quantité valide");
        return;
      }
      if (prixInvalide(produit)) {
        error(messagePrix(produit));
        return;
      }
      if (moduleGStockActif.value && !produit.magasin) {
        error("Veuillez sélectionner le magasin");
        return;
      }

      const ligne = { ...produit };
      if (enModification.value && modificationIndex.value !== null) {
        produits.value[modificationIndex.value] = ligne;
        enModification.value = false;
        modificationIndex.value = null;
      } else {
        produits.value.push(ligne);
      }
      resetLigne();
    };

    const editProduit = (index: number) => {
      enModification.value = true;
      modificationIndex.value = index;
      Object.assign(produit, produits.value[index]);
    };

    const removeRowProduit = (index: number) => {
      produits.value.splice(index, 1);
    };

    watch(
      produit,
      (newProduit: any) => {
        isDisable.value =
          validateRowProduit(newProduit.produit) ||
          validateRowProduit(newProduit.qtite) ||
          prixInvalide(newProduit);
      },
      { deep: true, immediate: true }
    );

    // Brouillon en sessionStorage
    const sauvegarderVenteEnCours = () => {
      try {
        sessionStorage.setItem(
          "venteEnCours",
          JSON.stringify({
            clientCharger: clientCharger.value,
            produits: produits.value,
            montantRecuSaisi: montantRecuSaisi.value,
            timestamp: new Date().getTime(),
          })
        );
      } catch (error) {
        console.error("Erreur lors de la sauvegarde de la vente:", error);
      }
    };

    const chargerVenteEnCours = () => {
      try {
        const savedData = sessionStorage.getItem("venteEnCours");
        if (savedData) {
          const venteData = JSON.parse(savedData);
          clientCharger.value = venteData.clientCharger;
          produits.value = venteData.produits || [];
          montantRecuSaisi.value = venteData.montantRecuSaisi ?? "";
        }
      } catch (error) {
        console.error("Erreur lors du chargement de la vente:", error);
        sessionStorage.removeItem("venteEnCours");
      }
    };

    const nettoyerVenteEnCours = () => {
      try {
        sessionStorage.removeItem("venteEnCours");
      } catch (error) {
        console.error("Erreur lors du nettoyage du sessionStorage:", error);
      }
    };

    watch(
      [clientCharger, produits, montantRecuSaisi],
      () => {
        if (!isSaving.value) {
          sauvegarderVenteEnCours();
        }
      },
      { deep: true }
    );

    const getPointVenteId = () => {
      try {
        return JwtService.getPointDeVenteId();
      } catch {
        return null;
      }
    };

    const resetFormData = () => {
      produits.value = [];
      montantRecuSaisi.value = "";
      clientCharger.value = null;
      selectedClientId.value = 0;
      selectedClient.value = false;
      resetLigne();
      nettoyerVenteEnCours();
    };

    const addVente = async (values: any, { resetForm }: any) => {
      if (!values.client) {
        error("Veuillez sélectionner un client");
        return null;
      }
      if (produits.value.length === 0) {
        error("Veuillez ajouter au moins un produit");
        return null;
      }

      const ligneInvalide = produits.value.find((p) => prixInvalide(p));
      if (ligneInvalide) {
        error(`Prix invalide pour « ${ligneInvalide.libelleProduit} » : ${messagePrix(ligneInvalide)}`);
        return null;
      }

      if (!ouvertureId.value) {
        error("Veuillez choisir la caisse qui encaisse cette vente (aucune caisse ouverte sélectionnée)");
        return null;
      }

      const total = totalCommande.value;

      if (montantInsuffisant.value) {
        error(`Le montant reçu (${montantRecuEffectif.value}) est inférieur au total (${total})`);
        return null;
      }

      const payload = {
        pointvente: getPointVenteId(),
        commande: {
          montantTotal: total,
          montantRecu: montantRecuEffectif.value, // le serveur recalcule le reliquat
          ouvertureId: ouvertureId.value, // session de caisse choisie
          dateCommande: values.dateCommande || getCurrentDate(),
          clientId: values.client,
          societeId: 1,
        },
        produits: produits.value.map((p) => {
          const montant = (Number(p.qtite) || 0) * (Number(p.prix) || 0);
          return {
            produit: p.produit.split("|")[0],
            libelleProduit: p.libelleProduit,
            qtite: p.qtite,
            prix: p.prix,
            montant,
            magasin: p.magasin,
          };
        }),
      };

      try {
        const response = await ApiService.post("/commandeClients", payload);

        if (response.data.code === 201) {
          isSaving.value = true;
          success(response.data.message);
          nettoyerVenteEnCours();
          resetForm();
          resetFormData();
          setTimeout(() => {
            isSaving.value = false;
          }, 100);
          router.push({ name: "ListeVente" });
          return response;
        }

        error(response.data.message || "Erreur lors de l'enregistrement");
        return null;
      } catch (err: any) {
        console.error("Erreur complète:", err);

        let errorMessage = "Erreur inattendue";
        let isStockError = false;

        if (err.response) {
          errorMessage =
            err.response.data?.message ||
            err.response.data?.errors ||
            err.response.statusText ||
            "Erreur lors de l'opération";
          isStockError = String(errorMessage).toLowerCase().includes("stock");
        } else if (err.message) {
          errorMessage = err.message;
        }

        if (isStockError) {
          warning(errorMessage);
        } else {
          error(errorMessage);
        }
        return null;
      }
    };

    onMounted(() => {
      fetchMagasin1();
      fetchProduits();
      fetchClients();
      chargerVenteEnCours();
      chargerOuvertures();

      // Config MODULE-G-STOCK
      ApiService.get("/all/configuration")
        .then((response) => {
          const stockConfig = response.data.data.find((c: any) => c.cle === "MODULE-G-STOCK");
          if (stockConfig) {
            moduleGStockActif.value = stockConfig.valeur;
          }
        })
        .catch(() => {
          // En cas d'erreur, on garde true par défaut
        });
    });

    return {
      venteSchema,
      addVente,
      getCurrentDate,
      clientOptions,
      clientCharger,
      selectedClient,
      selectedClientId,
      clientsOps,
      toggleAbonne,
      getClient,
      produitOptions,
      magasin1Options,
      produits,
      produit,
      totalCommande,
      ouvertures,
      ouvertureId,
      ouverturesChargees,
      formatDateOuverture,
      montantRecuSaisi,
      montantInsuffisant,
      reliquat,
      isDisable,
      isSaving,
      enModification,
      moduleGStockActif,
      validateRowProduit,
      prixInvalide,
      messagePrix,
      selectProd,
      updateStockByMagasin,
      addRowProduit,
      editProduit,
      removeRowProduit,
      getMagasinLabel,
    };
  },
});
</script>

<style scoped>
.cursor-not-allowed {
  cursor: not-allowed;
}

.info {
  min-width: 300px;
}

input[readonly] {
  background-color: #f0f0f0;
}

.table-responsive {
  overflow-x: auto;
}

.table tbody td {
  white-space: nowrap;
}

.produit-col {
  white-space: normal !important;
  word-wrap: break-word;
  max-width: 170px;
}

.actions-col {
  width: 120px;
  position: sticky;
  right: 0;
  background-color: white;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
}

.table thead th.actions {
  position: sticky;
  right: 0;
  background-color: white;
  z-index: 11;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
}

.form-control {
  width: 100%;
}

label {
  margin-bottom: 0.2rem;
}
</style>